/* ============================================================
   PWAInstallBanner.js — Complete Install Prompt
   • Android: uses beforeinstallprompt native API
   • iOS Safari: shows manual "Add to Home Screen" guide
   • Desktop Chrome/Edge: shows "Install App" button
   • Dismissible with 7-day cooldown (localStorage)
   ============================================================ */
import React, { useState, useEffect, useCallback } from 'react';

// Detect platform
const UA = navigator.userAgent;
const isIOS       = /iPhone|iPad|iPod/i.test(UA);
const isAndroid   = /Android/i.test(UA);
const isMobile    = isIOS || isAndroid;
const isInPWA     = window.matchMedia('(display-mode: standalone)').matches
                  || window.navigator.standalone === true;
const isSafari    = /Safari/i.test(UA) && !/Chrome/i.test(UA);
const DISMISS_KEY = 'wedoo_pwa_dismissed_until';

export default function PWAInstallBanner() {
  const [show,           setShow]           = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [mode,           setMode]           = useState(''); // 'mobile-android' | 'mobile-ios' | 'desktop'
  const [iosStep,        setIosStep]        = useState(false); // show iOS instructions modal

  useEffect(() => {
    // Already installed? Don't show
    if (isInPWA) return undefined;

    // Was dismissed recently?
    const dismissedUntil = localStorage.getItem(DISMISS_KEY);
    if (dismissedUntil && Date.now() < parseInt(dismissedUntil)) return undefined;

    // Desktop / Android: wait for beforeinstallprompt
    const handlePrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      const deviceMode = isMobile ? 'mobile-android' : 'desktop';
      setMode(deviceMode);
      // Show after 4 seconds so user sees the site first
      setTimeout(() => setShow(true), 4000);
    };
    window.addEventListener('beforeinstallprompt', handlePrompt);

    // iOS Safari: no beforeinstallprompt event — show manual guide
    if (isIOS && isSafari) {
      setMode('mobile-ios');
      setTimeout(() => setShow(true), 5000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handlePrompt);
  }, []);

  const handleInstall = useCallback(async () => {
    if (mode === 'mobile-ios') {
      setIosStep(true);
      return;
    }
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          dismiss();
          return;
        }
      } catch {}
    }
    dismiss();
  }, [deferredPrompt, mode]);

  const dismiss = (days = 7) => {
    setShow(false);
    setIosStep(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now() + days * 86400000));
  };

  if (!show && !iosStep) return null;

  /* ── iOS Instructions Modal ── */
  if (iosStep) return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      padding: '1rem',
    }} onClick={() => setIosStep(false)}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#1c1c1c', border: '1px solid rgba(245,168,0,0.3)',
        borderRadius: '16px 16px 0 0', padding: '2rem 1.75rem 2.5rem',
        width: '100%', maxWidth: '480px',
        animation: 'slideUp 0.4s cubic-bezier(0.25,0.46,0.45,0.94) both',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: '#F5A800', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Georgia, serif', fontWeight: '700', fontSize: '1.3rem', color: '#000000' }}>
          <img src="/logo192.png" alt="Wedoo" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'inherit'}} />
        </div>
            <div>
              <p style={{ fontWeight: '600', color: '#f8f5ef', fontSize: '0.95rem' }}>Wedoo Interiors</p>
              <p style={{ color: '#8a8278', fontSize: '0.72rem' }}>Add to Home Screen</p>
            </div>
          </div>
          <button onClick={() => { setIosStep(false); dismiss(); }} style={{ color: '#8a8278', fontSize: '1.3rem', lineHeight: 1 }}>✕</button>
        </div>

        <p style={{ color: '#ede8de', fontSize: '0.88rem', lineHeight: '1.7', marginBottom: '1.75rem', textAlign: 'center' }}>
          Install Wedoo Interiors on your iPhone for the best experience — browse designs, get quotes & contact us instantly.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {[
            { step: '1', icon: '⬆', title: 'Tap the Share button', desc: 'Find it at the bottom of your Safari browser' },
            { step: '2', icon: '➕', title: 'Tap "Add to Home Screen"', desc: 'Scroll down in the share sheet to find this option' },
            { step: '3', icon: '✓',  title: 'Tap "Add"',             desc: 'Wedoo Interiors will appear on your home screen' },
          ].map(s => (
            <div key={s.step} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#F5A800', color: '#000000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '1rem', flexShrink: 0 }}>
                {s.icon}
              </div>
              <div>
                <p style={{ color: '#f8f5ef', fontWeight: '500', fontSize: '0.88rem' }}>{s.title}</p>
                <p style={{ color: '#8a8278', fontSize: '0.75rem', marginTop: '0.2rem' }}>{s.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Animated arrow pointing to Safari share button */}
        <div style={{ textAlign: 'center', marginTop: '1.5rem', color: '#F5A800', fontSize: '0.78rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
          Look for the ⬆ icon in your Safari toolbar
        </div>
      </div>
      <style>{`@keyframes slideUp { from { transform:translateY(100%); opacity:0; } to { transform:translateY(0); opacity:1; } }`}</style>
    </div>
  );

  /* ── Mobile Banner (Android) ── */
  if (isMobile) return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9990,
      background: '#141414',
      borderTop: '1px solid rgba(245,168,0,0.25)',
      padding: '0.85rem 1.25rem',
      display: 'flex', alignItems: 'center', gap: '0.85rem',
      boxShadow: '0 -4px 30px rgba(0,0,0,0.5)',
      animation: 'slideUpBanner 0.4s ease both',
    }}>
      {/* App Icon */}
      <div style={{
        width: '52px', height: '52px', borderRadius: '12px', flexShrink: 0,
        background: 'linear-gradient(135deg, #F5A800, #9a7a34)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'Georgia,serif', fontWeight: '700', fontSize: '1.5rem', color: '#000000',
        boxShadow: '0 4px 12px rgba(245,168,0,0.3)',
      }}>
          <img src="/logo192.png" alt="Wedoo" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'inherit'}} />
        </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontWeight: '600', color: '#f8f5ef', fontSize: '0.88rem', lineHeight: 1.2 }}>Wedoo Interiors</p>
        <p style={{ color: '#8a8278', fontSize: '0.72rem', marginTop: '0.2rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {mode === 'mobile-ios' ? 'Add to Home Screen for instant access' : 'Install app for the best experience'}
        </p>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, alignItems: 'center' }}>
        <button onClick={handleInstall} style={{
          background: '#F5A800', color: '#000000', border: 'none',
          padding: '0.55rem 1.25rem', fontSize: '0.72rem', fontWeight: '600',
          letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer',
          borderRadius: '2px',
        }}>
          {mode === 'mobile-ios' ? 'How to' : 'Install'}
        </button>
        <button onClick={() => dismiss()} style={{ color: '#8a8278', fontSize: '1.2rem', lineHeight: 1, padding: '0.25rem 0.35rem' }}>✕</button>
      </div>

      <style>{`
        @keyframes slideUpBanner { from { transform:translateY(100%); } to { transform:translateY(0); } }
      `}</style>
    </div>
  );

  /* ── Desktop Install Popup ── */
  return (
    <div style={{
      position: 'fixed', bottom: '1.5rem', right: '1.5rem', zIndex: 9990,
      background: '#1c1c1c',
      border: '1px solid rgba(245,168,0,0.3)', borderRadius:'var(--radius)',
      boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      width: '340px',
      animation: 'slideInRight 0.45s cubic-bezier(0.25,0.46,0.45,0.94) both',
    }}>
      {/* Gold top bar */}
      <div style={{ height: '3px', background: 'linear-gradient(90deg, #F5A800, #FFB800)' }} />

      <div style={{ padding: '1.5rem' }}>
        {/* Close */}
        <button onClick={() => dismiss()} style={{ position: 'absolute', top: '1rem', right: '1rem', color: '#8a8278', fontSize: '1.1rem', lineHeight: 1 }}>✕</button>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
          <div style={{
            width: '52px', height: '52px', borderRadius: '10px', flexShrink: 0,
            background: 'linear-gradient(135deg, #F5A800, #9a7a34)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'Georgia,serif', fontWeight: '700', fontSize: '1.5rem', color: '#000000',
          }}>
          <img src="/logo192.png" alt="Wedoo" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'inherit'}} />
        </div>
          <div>
            <p style={{ fontWeight: '600', color: '#f8f5ef', fontSize: '0.95rem' }}>Wedoo Interiors</p>
            <p style={{ color: '#8a8278', fontSize: '0.72rem' }}>Install as Desktop App</p>
          </div>
        </div>

        <p style={{ color: '#ede8de', fontSize: '0.82rem', lineHeight: '1.7', marginBottom: '1.25rem' }}>
          Install Wedoo Interiors for faster access — works offline, no browser needed, and opens like a native app.
        </p>

        {/* Feature chips */}
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
          {['⚡ Fast', '🔔 Notifications', '📱 Offline', '🖥 Desktop icon'].map(f => (
            <span key={f} style={{ padding: '0.25rem 0.6rem', background: 'rgba(245,168,0,0.08)', border: '1px solid rgba(245,168,0,0.2)', color: '#F5A800', fontSize: '0.65rem', borderRadius: '20px' }}>{f}</span>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button onClick={handleInstall} style={{
            flex: 1, background: '#F5A800', color: '#000000', border: 'none',
            padding: '0.8rem', fontSize: '0.72rem', fontWeight: '600',
            letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer',
          }}>
            Install App
          </button>
          <button onClick={() => dismiss(30)} style={{
            padding: '0.8rem 1rem', background: 'transparent',
            border: '1px solid rgba(255,255,255,0.1)', borderRadius:'var(--radius)',
            color: '#8a8278', fontSize: '0.72rem', cursor: 'pointer',
          }}>
            Not now
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight { from { transform:translateX(120%); opacity:0; } to { transform:translateX(0); opacity:1; } }
      `}</style>
    </div>
  );
}
